import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import Navbar from "../components/marketing/Navbar";
import Footer from "../components/marketing/Footer";
import { FEATURES, HERO_STATS, LOGOS, TESTIMONIALS, PILLARS } from "../mock";

function FeatureIcon({ name }) {
  const C = Icons[name] || Icons.Sparkles;
  return <C size={22} className="text-coral" />;
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      {/* HERO */}
      <section className="hero-radial relative">
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-white border border-cream text-forest">
              <span className="h-1.5 w-1.5 rounded-full bg-coral" />
              A product by The Bench
            </div>
            <h1 className="font-display mt-5 text-5xl md:text-6xl leading-[1.02]">
              Plan, manage and <span className="text-coral">monitor</span> every project. Bill for every hour.
            </h1>
            <p className="mt-6 text-lg text-[#3f4a44] max-w-xl">
              Full Bench is the complete kit for consulting professionals who bill on time and expense. One tool for the timer, the budget, the timesheet and the invoice.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/signup" className="btn-coral px-6 py-3 rounded-md text-sm font-medium text-center">
                Try Full Bench free
              </Link>
              <Link to="/features" className="btn-outline-forest px-6 py-3 rounded-md text-sm font-medium text-center">
                See how it works
              </Link>
            </div>
            <p className="mt-4 text-xs text-[#6a736f]">Free for 30 days — no credit card needed.</p>
          </div>

          {/* Right side mock product card */}
          <div className="relative">
            <div className="bg-white rounded-2xl card-shadow p-5 border border-cream">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Today — Wednesday</div>
                <span className="text-xs px-2 py-1 rounded-full bg-cream-2 text-forest">6h 15m logged</span>
              </div>
              <div className="mt-4 divide-y divide-[#EAF3EE]">
                {[
                  { p: "Station precinct bid", t: "Advisory", h: "2:30", c: "#22C55E" },
                  { p: "Procurement strategy", t: "Cost engineering", h: "1:45", c: "#143A25" },
                  { p: "Contract review — D&C", t: "Contract review", h: "1:00", c: "#0E9F6E" },
                  { p: "Risk & claims support", t: "Meetings", h: "1:00", c: "#4ADE80" },
                ].map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.c }} />
                      <div>
                        <div className="text-sm font-medium">{r.p}</div>
                        <div className="text-xs text-[#6a736f]">{r.t}</div>
                      </div>
                    </div>
                    <div className="text-sm tabular-nums">{r.h}</div>
                  </div>
                ))}
              </div>
              <button className="mt-4 w-full btn-coral rounded-md py-2.5 text-sm font-medium flex items-center justify-center gap-2">
                <Icons.Play size={16} /> Start timer
              </button>
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-6 bg-white rounded-xl card-shadow p-4 border border-cream w-56">
              <div className="text-xs text-[#6a736f]">This week</div>
              <div className="font-display text-2xl">32h 20m</div>
              <div className="mt-2 h-2 rounded-full bg-[#EAF3EE] overflow-hidden">
                <div className="h-full bg-coral" style={{ width: "72%" }} />
              </div>
              <div className="mt-1 text-xs text-[#6a736f]">72% of 45h budget</div>
            </div>
          </div>
        </div>

        {/* Pillars */}
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pb-14 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <div key={p.k} className="bg-white rounded-xl border border-cream p-6">
              <div className="flex items-center gap-2 text-xs text-coral font-semibold uppercase tracking-wide">
                <span className="h-5 w-5 rounded-full bg-cream-2 text-forest flex items-center justify-center text-[10px]">{i + 1}</span>
                {p.k}
              </div>
              <div className="font-display text-2xl mt-3">{p.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Logos */}
      <section className="border-y border-cream bg-cream-2">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-80">
          {LOGOS.map((l) => (
            <div key={l} className="font-display text-lg text-forest/70">{l}</div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="max-w-2xl">
          <div className="text-sm text-coral font-semibold uppercase tracking-wide">Everything you need</div>
          <h2 className="font-display text-4xl md:text-5xl mt-2">One tool from timer to paid invoice.</h2>
          <p className="mt-4 text-[#3f4a44]">Built for consulting professionals who bill on time and expense — from solo advisors to full firms.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-xl border border-cream p-6 hover:-translate-y-0.5 transition-transform card-shadow">
              <div className="h-10 w-10 rounded-md bg-cream-2 flex items-center justify-center">
                <FeatureIcon name={f.icon} />
              </div>
              <div className="mt-4 font-semibold text-lg">{f.title}</div>
              <p className="mt-2 text-sm text-[#3f4a44]">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Big feature strip - invoicing focus */}
      <section className="bg-forest text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-sm text-coral font-semibold uppercase tracking-wide">Invoicing</div>
            <h3 className="font-display text-4xl mt-2">Every hour, one click from paid.</h3>
            <p className="mt-4 text-white/80">Full Bench turns tracked time into a professional invoice in seconds. Send it, get paid online, and reconcile it against your project budget without ever leaving the app.</p>
            <ul className="mt-6 space-y-2 text-sm">
              {["Draft invoices from tracked time","Accept Stripe & PayPal payments","Auto-remind late clients","Sync with Xero and MYOB"].map((b) => (
                <li key={b} className="flex items-center gap-2"><Icons.Check size={16} className="text-coral" /> {b}</li>
              ))}
            </ul>
            <Link to="/signup" className="btn-coral inline-block mt-8 px-6 py-3 rounded-md text-sm font-medium">Start invoicing today</Link>
          </div>
          <div className="bg-white rounded-2xl p-6 text-[#1F2F27] card-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display text-2xl">Invoice INV-2026-015</div>
                <div className="text-xs text-[#6a736f]">Cedar Advisory — due Jun 25</div>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full bg-cream-2 text-coral font-medium">Sent</span>
            </div>
            <table className="w-full mt-5 text-sm">
              <thead className="text-xs text-[#6a736f]">
                <tr className="text-left"><th className="py-2">Item</th><th>Hours</th><th className="text-right">Amount</th></tr>
              </thead>
              <tbody className="divide-y divide-[#EAF3EE]">
                <tr><td className="py-2">Advisory</td><td>44.5</td><td className="text-right tabular-nums">$12,682</td></tr>
                <tr><td className="py-2">Cost engineering</td><td>28</td><td className="text-right tabular-nums">$6,720</td></tr>
                <tr><td className="py-2">Project management</td><td>32</td><td className="text-right tabular-nums">$6,998</td></tr>
              </tbody>
              <tfoot>
                <tr><td colSpan={2} className="pt-3 text-right font-semibold">Total</td><td className="pt-3 text-right font-semibold">$26,400</td></tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <h3 className="font-display text-4xl max-w-2xl">Trusted by consultants who bill on time and expense.</h3>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="bg-white rounded-xl border border-cream p-6">
              <Icons.Quote size={18} className="text-coral" />
              <p className="mt-3 text-[#1F2F27]">“{t.quote}”</p>
              <div className="mt-5 text-sm">
                <div className="font-semibold">{t.name}</div>
                <div className="text-[#6a736f]">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Compare CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20">
        <div className="rounded-2xl bg-cream-2 border border-cream p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-sm text-coral font-semibold uppercase tracking-wide">Shopping around?</div>
            <h3 className="font-display text-3xl mt-2">See how Full Bench compares.</h3>
            <p className="mt-2 text-[#3f4a44] max-w-xl">Honest side-by-side comparisons with Harvest, Toggl, Clockify, Hubstaff, FreshBooks and more.</p>
          </div>
          <Link to="/compare" className="btn-outline-forest px-6 py-3 rounded-md text-sm font-medium">Browse comparisons</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
